import React, { useEffect, useRef, useState } from "react"

const COLORS = [
  "#7c3aed",
  "#a78bfa",
  "#10b981",
  "#34d399",
  "#f59e0b",
  "#fbbf24",
  "#ec4899",
  "#38bdf8",
]

export default function RewardPopup({
  open = true,
  imageSrc = "https://training-asset.sgp1.digitaloceanspaces.com/WONDERBOT_RENDER__2_0007.png",
  badgeText = "✦ Secret prize unlocked ✦",
  eyebrowText = "Congratulations",
  headline = "You have been rewarded!",
  subtext = "A special surprise just for you 🎁",
  onClose,
}) {
  const confettiRef = useRef(null)
  const [isVisible, setIsVisible] = useState(open)
  const [isClosing, setIsClosing] = useState(false)

  useEffect(() => {
    setIsVisible(open)
    if (open) {
      setIsClosing(false)
    }
  }, [open])

  useEffect(() => {
    if (!isVisible || !confettiRef.current) return

    const container = confettiRef.current
    container.innerHTML = ""

    for (let i = 0; i < 32; i++) {
      const el = document.createElement("div")
      el.className = "reward-confetti-piece"

      const dur = (1.2 + Math.random() * 1.1).toFixed(2) + "s"
      const delay = (Math.random() * 0.65).toFixed(2) + "s"

      el.style.left = `${Math.random() * 100}%`
      el.style.top = `${Math.random() * 20}%`
      el.style.background = COLORS[Math.floor(Math.random() * COLORS.length)]
      el.style.animation = `confettiFall ${dur} ease-out ${delay} forwards`

      container.appendChild(el)
    }

    return () => {
      container.innerHTML = ""
    }
  }, [isVisible])

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      setIsVisible(false)
      if (onClose) onClose()
    }, 320)
  }

  if (!isVisible) return null

  return (
    <>
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&display=swap");

        * {
          box-sizing: border-box;
        }

        @keyframes confettiFall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(260px) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes ringRotate {
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes popIn {
          0% {
            transform: scale(0.88) translateY(20px);
            opacity: 0;
          }
          70% {
            transform: scale(1.03) translateY(-4px);
            opacity: 1;
          }
          100% {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }

        @keyframes floatBadge {
          0%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-5px);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .reward-overlay {
          position: fixed;
          inset: 0;
          background: rgba(8, 4, 24, 0.78);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          animation: fadeIn 0.3s ease both;
          opacity: ${isClosing ? 0 : 1};
          transition: opacity 0.3s ease;
          font-family: "Sora", sans-serif;
        }

        .reward-card {
          position: relative;
          background: linear-gradient(
            160deg,
            #13063a 0%,
            #091840 55%,
            #051a10 100%
          );
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 28px;
          padding: 48px 40px 36px;
          max-width: 380px;
          width: 90vw;
          text-align: center;
          box-shadow:
            0 0 80px rgba(100, 50, 255, 0.28),
            0 0 160px rgba(30, 180, 80, 0.1);
          animation: popIn 0.48s cubic-bezier(0.34, 1.56, 0.64, 1) both;
          overflow: visible;
        }

        .reward-confetti-container {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          border-radius: inherit;
        }

        .reward-confetti-piece {
          position: absolute;
          width: 7px;
          height: 7px;
          border-radius: 2px;
          opacity: 0;
        }

        .reward-badge {
          position: absolute;
          top: -18px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(90deg, #7c3aed, #059669);
          border-radius: 99px;
          padding: 5px 18px;
          font-size: 11px;
          font-weight: 600;
          color: #fff;
          letter-spacing: 0.1em;
          white-space: nowrap;
          animation: floatBadge 2.4s ease-in-out infinite;
        }

        .reward-img-wrap {
          position: relative;
          width: 148px;
          height: 148px;
          margin: 0 auto 24px;
        }

        .reward-ring {
          position: absolute;
          inset: -8px;
          border-radius: 50%;
          background: conic-gradient(
            from 0deg,
            #ff6edf,
            #7b5fff,
            #00e0ff,
            #39ff91,
            #ffd700,
            #ff6edf
          );
          animation: ringRotate 3s linear infinite;
        }

        .reward-ring-mask {
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          background: #0d1040;
        }

        .reward-img {
          position: relative;
          z-index: 1;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid rgba(255, 255, 255, 0.12);
        }

        .reward-eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #a78bfa;
          margin-bottom: 8px;
        }

        .reward-headline {
          font-size: 24px;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 8px;
          background: linear-gradient(90deg, #fff 20%, #a5f3c8 60%, #c4b5fd 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .reward-subtext {
          font-size: 13px;
          color: rgba(200, 190, 230, 0.6);
          margin-bottom: 0;
        }

        .reward-close-btn {
          position: absolute;
          top: 16px;
          right: 18px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 50%;
          width: 32px;
          height: 32px;
          color: rgba(255, 255, 255, 0.6);
          font-size: 16px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s, color 0.15s;
        }

        .reward-close-btn:hover {
          background: rgba(255, 255, 255, 0.16);
          color: #fff;
        }
      `}</style>

      <div
        className="reward-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose()
        }}
      >
        <div className="reward-card">
          <div ref={confettiRef} className="reward-confetti-container" />

          <div className="reward-badge">{badgeText}</div>

          <button
            className="reward-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>

          <div className="reward-img-wrap">
            <div className="reward-ring" />
            <div className="reward-ring-mask" />
            <img className="reward-img" src={imageSrc} alt="Your reward" />
          </div>

          <p className="reward-eyebrow">{eyebrowText}</p>
          <h2 className="reward-headline">{headline}</h2>
          <p className="reward-subtext">{subtext}</p>
        </div>
      </div>
    </>
  )
}
